import { Button, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { getFragmentData } from '@api/gql';
import { usePagePagination } from '@common/hooks/use-page-pagination';

import TablePagination from '@components/TablePagination';

import { standingAccessReportEntitlementLinkFragmentDocument } from '@StandingAccess/services/gql';
import {
  useDownloadStandingAccessReportEntitlementLinksCSV,
  useGetStandingAccessReportEntitlementLinks,
} from '@StandingAccess/services/standingAccessQueries';
import AssessmentReportAccessRevokeTable from '@StandingAccess/components/AssessmentReportAccessRevokeTable';

import AssessmentReportFilters from './AssessmentReportFilters';
import { useState, useCallback, useEffect } from 'react';
import AssessmentReportRevokeSelection from './AssessmentReportRevokeSelection';
import { StandingAccessReportEntitlementLinkFragmentFragment } from '@api/gql/graphql';
import AssessmentReportRevokeModal from './AssessmentReportRevokeModal';
import { AssessmentReportReassessButton } from './AssessmentReportReassess';
import { RouterLink } from '@libs/router';
import { useReportFilters } from '@StandingAccess/hooks/use-report-filters';
import {
  ASSESSMENT_REPORT_RISK_SCORE_FILTER,
  ASSESSMENT_REPORT_PERMISSIONS_FILTER,
  ASSESSMENT_REPORT_RESOURCES_FILTER,
  ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER,
  FORCE_REVOKE_PARAM,
  ASSESSMENT_REPORT_TIER_FILTER,
} from '@StandingAccess/types';

export interface AssessmentReportAccessRevokeProps {
  reportId: string;
  tier?: string[];
  backPath?: string;
}

function useAssessmentReportAccessRevokeSelection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState<string[]>([]);
  const [realSelectedAll, setRealSelectAll] = useState(() => {
    const forceRevoke = searchParams.get(FORCE_REVOKE_PARAM);
    return forceRevoke === 'true';
  });
  const [triggeredSelectAll, setTriggeredSelectAll] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(() => {
    const forceRevoke = searchParams.get(FORCE_REVOKE_PARAM);
    return forceRevoke === 'true';
  });

  useEffect(() => {
    if (selected.length === 0 && !searchParams.get(FORCE_REVOKE_PARAM)) {
      setTriggeredSelectAll(false);
      setRealSelectAll(false);
    }
  }, [selected, searchParams]);

  const handleSelect = useCallback((itemId: string) => {
    setSelected((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });

    setTriggeredSelectAll(false);
    setRealSelectAll(false);
  }, []);

  const handleClear = useCallback(() => {
    setSelected([]);
    searchParams.delete(FORCE_REVOKE_PARAM);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const toggleAll = useCallback(
    (items: StandingAccessReportEntitlementLinkFragmentFragment[]) => {
      const itemIds = items.map((item) => item.id);
      const allSelected = itemIds.every((id) => selected.includes(id));
      setSelected((prev) =>
        allSelected ? prev.filter((id) => !itemIds.includes(id)) : [...new Set([...prev, ...itemIds])],
      );
    },
    [selected],
  );

  const handleSelectAll = useCallback(
    (items: StandingAccessReportEntitlementLinkFragmentFragment[]) => {
      toggleAll(items);
      setTriggeredSelectAll(true);
    },
    [toggleAll],
  );

  const handleRealSelectAll = useCallback(() => {
    setRealSelectAll(true);
  }, []);

  return {
    selected,
    triggeredSelectAll,
    realSelectedAll,
    handleSelect,
    handleClear,
    handleSelectAll,
    handleRealSelectAll,
    revokeModalOpen,
    setRevokeModalOpen,
  };
}

function useAssessmentReportAccessRevoke({
  reportId,
  selected,
  realSelectedAll,
}: AssessmentReportAccessRevokeProps & {
  selected: string[];
  realSelectedAll: boolean;
}) {
  const { offset, pageSize, handlePageChange } = usePagePagination();
  const filter = useReportFilters();

  if (filter.tier) {
    filter.revoked = false;
  }

  const {
    data,
    isFetched,
    isFetching: isLoading,
  } = useGetStandingAccessReportEntitlementLinks({
    reportId,
    filter,
    pagination: { offset, take: pageSize },
  });

  const selection = realSelectedAll ? { selectAll: true } : selected.length > 0 ? { ids: selected } : undefined;

  const { downloadCSV, isDownloadingCSV } = useDownloadStandingAccessReportEntitlementLinksCSV({
    reportId,
    filter,
    selection,
  });

  const items = data?.standingAccessReportEntitlementLinks.edges.map((edge) =>
    getFragmentData(standingAccessReportEntitlementLinkFragmentDocument, edge.node),
  );

  const totalCount = data?.standingAccessReportEntitlementLinks.totalCount ?? 0;

  const handleExportCSV = useCallback(() => {
    downloadCSV();
  }, [downloadCSV]);

  return {
    items,
    isFetching: !isFetched,
    isLoading,
    pageSize,
    offset,
    totalCount,
    isDownloadingCSV,
    handlePageChange,
    handleExportCSV,
  };
}

export default function AssessmentReportAccessRevoke({ reportId, backPath }: AssessmentReportAccessRevokeProps) {
  const {
    selected,
    triggeredSelectAll,
    realSelectedAll,
    handleSelect,
    handleClear,
    handleSelectAll,
    handleRealSelectAll,
    revokeModalOpen,
    setRevokeModalOpen,
  } = useAssessmentReportAccessRevokeSelection();

  const {
    items,
    isFetching,
    isLoading,
    pageSize,
    offset,
    handlePageChange,
    totalCount,
    isDownloadingCSV,
    handleExportCSV,
  } = useAssessmentReportAccessRevoke({
    reportId,
    selected,
    realSelectedAll,
  });

  return (
    <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
      {selected.length > 0 || realSelectedAll ? (
        <AssessmentReportRevokeSelection
          selected={selected}
          total={totalCount}
          triggeredSelectAll={triggeredSelectAll}
          realSelectedAll={realSelectedAll}
          onClear={handleClear}
          onRealSelectAll={handleRealSelectAll}
          onRevoke={() => setRevokeModalOpen(true)}
        />
      ) : (
        <AssessmentReportFilters
          reportId={reportId}
          allowedFilters={[
            ASSESSMENT_REPORT_TIER_FILTER.param,
            ASSESSMENT_REPORT_RESOURCES_FILTER.param,
            ASSESSMENT_REPORT_PERMISSIONS_FILTER.param,
            ASSESSMENT_REPORT_RISK_SCORE_FILTER.param,
            ASSESSMENT_REPORT_LINKED_ELEMENTS_FILTER.param,
          ]}
        />
      )}
      <AssessmentReportRevokeModal
        open={revokeModalOpen}
        isSelectedItems={selected.length > 0 || realSelectedAll}
        onClose={() => setRevokeModalOpen(false)}
        onExportCSV={handleExportCSV}
        exportLoading={isDownloadingCSV}
        actions={
          <Stack direction="row" spacing={1} justifyContent="space-between">
            {backPath && (
              <Button variant="outlined" component={RouterLink} to={backPath}>
                Back to Assessment
              </Button>
            )}
            <AssessmentReportReassessButton reportId={reportId} />
          </Stack>
        }
      />
      <AssessmentReportAccessRevokeTable
        items={items ?? []}
        isLoading={isLoading}
        isFetching={isFetching}
        selected={selected}
        realSelectedAll={realSelectedAll}
        onSelect={handleSelect}
        onSelectAll={() => handleSelectAll(items ?? [])}
      />
      <TablePagination
        pagination={{
          total: totalCount,
          limit: pageSize,
          offset,
        }}
        isLoading={isLoading}
        onChange={handlePageChange}
      />
    </Stack>
  );
}
